/**
 * Auth Context - 认证状态管理
 * 提供登录、登出、用户信息等认证相关功能
 */

import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { loginApi, getCaptcha, selectTenantApi, savePortalPreference } from '../api';
import type {
  LoginParams,
  LoginResultModel,
  SelectTenantParams,
  PortalPreferenceParams,
} from '../api';
import menuApi from '@/api/modules/menu';
import unifiedApi from '@/utils/unifiedApi';
import {
  getStorageItem,
  getToken,
  setStorageItem,
  setToken,
  setRefreshToken,
  clearUserData,
  STORAGE_KEYS,
} from '@/utils/storage';
import { sendLogoutToMall, listenForAuthMessages, requestTokenFromOpener } from '@/utils/authSync';

/**
 * REQ-20260510-0008 [CR-Low-L2]：DEV-only 日志助手
 *
 * 生产构建中 import.meta.env.DEV=false，Vite tree-shake 整段调用为死代码，
 * dist 产物 grep -F "console.log" 应零匹配。
 * console.warn / console.error 保留（用户排障价值高，且数量少）。
 */
const devLog = (...args: unknown[]): void => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

/**
 * 检查 JWT token 是否已过期（本地解析，不发请求）
 */
function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return false; // 没有 exp 字段，认为不过期
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

/**
 * REQ-20260508-0001: 登录返回值 — 区分 Case A (单租户) / Case B (多租户中间态)
 */
export type LoginOutcome =
  | { kind: 'success'; user: any }
  | { kind: 'tenant-select'; selectTenantToken: string; availableTenants: any[] }
  | { kind: 'failure'; reason?: string };

interface AuthContextProps {
  login: (params: LoginParams) => Promise<LoginOutcome>;
  /** REQ-20260508-0001 #10173: 多租户用户选定租户后换最终 token */
  selectTenant: (tenantId: number) => Promise<LoginOutcome>;
  /** REQ-20260508-0001 #10174: 保存当前用户门户偏好 */
  savePortalPreference: (params: PortalPreferenceParams) => Promise<boolean>;
  getCaptcha: () => Promise<any>;
  loading: boolean;
  menu: any[];
  user: any;
  /** REQ-20260508-0001: 当前用户可访问门户列表 */
  availablePortals: string[];
  /** REQ-20260508-0001: 当前用户偏好门户 */
  defaultPortal: string;
  logout: () => void;
  isAuthenticated: () => boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(false);
  // 同步从 localStorage 水合 user/menu，避免首帧 user=null 导致权限派生空窗 →
  // 登录(整页重载路径)时闪一帧"无权限/登录失败"再显示 dashboard 的问题。
  // 守卫/hasRole/useUserType 都读 localStorage(同步)不闪，唯 usePermission 读 context user。
  const [menu, setMenu] = useState<any[]>(() => {
    try {
      const token = getToken();
      if (!token || isTokenExpired(token)) return [];
      const m = getStorageItem(STORAGE_KEYS.MENU_DATA, [] as unknown[]);
      return Array.isArray(m) ? m : [];
    } catch {
      return [];
    }
  });
  const [user, setUser] = useState<any>(() => {
    try {
      const token = getToken();
      if (!token || isTokenExpired(token)) return null;
      return getStorageItem<Record<string, unknown> | null>(STORAGE_KEYS.USER_INFO, null);
    } catch {
      return null;
    }
  });

  /**
   * 把成功登录的 LoginResultModel 应用到 state + storage（共享逻辑：login / selectTenant）
   */
  const applyAuthSuccess = async (data: LoginResultModel): Promise<any | null> => {
    const { accessToken, userInfo: userInfoData, refreshToken } = data;
    if (!accessToken || !userInfoData) {
      console.warn('⚠️ 登录响应缺 access_token 或 user_info');
      return null;
    }

    localStorage.setItem(STORAGE_KEYS.TOKEN, accessToken);
    if (refreshToken) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    }

    // 角色码兼容多种格式
    const rawRoles = userInfoData.roles || [];
    const roleCodes: string[] = rawRoles
      .map((r: any) => (typeof r === 'string' ? r : r.roleCode || r.code || ''))
      .filter(Boolean);

    const userInfo = {
      id: userInfoData.userId,
      username: userInfoData.username,
      tenant_id: userInfoData.tenantId,
      tenant_code: userInfoData.tenantCode,
      tenant_name: userInfoData.tenantName,
      real_name: userInfoData.realName,
      email: userInfoData.email,
      phone: userInfoData.phone,
      avatar: userInfoData.avatar,
      user_scope: userInfoData.userScope || 'tenant',
      roles: rawRoles,
      role_codes: roleCodes,
      permissions: userInfoData.permissions || [],
      // 分销层级：0=顶级租户（品牌商），1+=下级客户；不兜底 0，undefined 表示旧后端未返回
      distribution_level: userInfoData.distributionLevel,
      // REQ-20260508-0001: 门户路由相关
      available_portals: userInfoData.availablePortals || [],
      default_portal: userInfoData.defaultPortal || '',
    };
    setStorageItem(STORAGE_KEYS.USER_INFO, userInfo);
    setUser(userInfo);

    // 菜单不在此处预取：侧边栏由 TenantLayout/useMenuItems 按 work_mode 拉取（带缓存），
    // 这里再拉一次 /menu/user 属于重复请求；刷新恢复走 initializeFromStorage 的兜底逻辑。

    // 清掉多租户中间态（已确定登录到具体租户）
    sessionStorage.removeItem(STORAGE_KEYS.SELECT_TENANT_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.AVAILABLE_TENANTS);

    return userInfo;
  };

  // 登录方法（REQ-20260508-0001: 双形态响应处理）
  const login = async (params: LoginParams): Promise<LoginOutcome> => {
    setLoading(true);
    try {
      devLog('🔐 尝试登录:', { username: params.username });
      const response = await loginApi(params);

      const isSuccess =
        response &&
        ((Number(response.code) === 200 && response.success !== false) ||
          response.success === true);
      if (!isSuccess || !response.data) {
        return { kind: 'failure', reason: response?.message };
      }

      const data = response.data as LoginResultModel;

      // Case B: 多租户中间态
      if (data.requireTenantSelect && data.selectTenantToken) {
        devLog('🏢 多租户登录 — 跳 /tenant-select');
        sessionStorage.setItem(STORAGE_KEYS.SELECT_TENANT_TOKEN, data.selectTenantToken);
        sessionStorage.setItem(
          STORAGE_KEYS.AVAILABLE_TENANTS,
          JSON.stringify(data.availableTenants || [])
        );
        return {
          kind: 'tenant-select',
          selectTenantToken: data.selectTenantToken,
          availableTenants: data.availableTenants || [],
        };
      }

      // Case A: 单租户最终态
      const userInfo = await applyAuthSuccess(data);
      return userInfo
        ? { kind: 'success', user: userInfo }
        : { kind: 'failure', reason: 'invalid response' };
    } catch (error: any) {
      console.error('❌ 登录失败:', error);
      return { kind: 'failure', reason: error?.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * REQ-20260508-0001 #10173: 多租户用户选定租户后换最终 token
   */
  const selectTenant = async (tenantId: number): Promise<LoginOutcome> => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem(STORAGE_KEYS.SELECT_TENANT_TOKEN);
      if (!token) {
        return { kind: 'failure', reason: '租户选择会话已失效，请重新登录' };
      }
      const params: SelectTenantParams = {
        select_tenant_token: token,
        tenant_id: tenantId,
      };
      const response = await selectTenantApi(params);
      if (!response || !response.data) {
        return { kind: 'failure', reason: response?.message };
      }
      const userInfo = await applyAuthSuccess(response.data as LoginResultModel);
      return userInfo
        ? { kind: 'success', user: userInfo }
        : { kind: 'failure', reason: 'invalid response' };
    } catch (error: any) {
      console.error('❌ 选择租户失败:', error);
      return { kind: 'failure', reason: error?.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * REQ-20260508-0001 #10174: 保存当前用户的门户偏好
   * remember=true 写库；false 仅 sessionStorage 临时保留（不下发后端）
   */
  const handleSavePortalPreference = async (params: PortalPreferenceParams): Promise<boolean> => {
    try {
      // remember=false 时仅前端 session 保留，不调用后端 API
      if (params.remember === false) {
        sessionStorage.setItem(STORAGE_KEYS.PORTAL_PREFERENCE_SESSION, params.default_portal);
        return true;
      }
      const response = await savePortalPreference(params);
      const ok =
        response &&
        ((Number(response.code) === 200 && response.success !== false) ||
          response.success === true);
      if (ok) {
        // 同步更新本地 user.default_portal
        if (user) {
          const updated = { ...user, default_portal: params.default_portal };
          setStorageItem(STORAGE_KEYS.USER_INFO, updated);
          setUser(updated);
        }
      }
      return !!ok;
    } catch (error) {
      console.error('保存门户偏好失败:', error);
      return false;
    }
  };

  // 初始化 - 从localStorage恢复数据
  useEffect(() => {
    const initializeFromStorage = async () => {
      try {
        // 检查本地存储中是否有用户信息或 token
        const token = getToken();
        const userInfo = getStorageItem(STORAGE_KEYS.USER_INFO);

        devLog('🔄 AuthContext初始化检查:', {
          hasToken: !!token,
          hasUserInfo: !!userInfo,
          userInfo,
        });

        if (!token || !userInfo) {
          devLog('⚠️ 本地存储中没有用户数据，跳过初始化');
          return;
        }

        // 检查 token 是否已过期
        if (isTokenExpired(token)) {
          devLog('🔒 Token 已过期，清理本地存储，跳过初始化');
          clearUserData();
          return;
        }

        devLog('✅ 从本地存储恢复用户数据');
        setLoading(true);

        // 立即恢复用户数据到状态（同步操作）
        setUser(userInfo);
        devLog('✅ User状态已设置:', userInfo);

        // 恢复菜单数据
        const cachedMenu = getStorageItem(STORAGE_KEYS.MENU_DATA);
        if (cachedMenu && Array.isArray(cachedMenu)) {
          setMenu(cachedMenu);
        } else {
          // 如果没有缓存菜单或缓存无效，重新获取
          try {
            const res = await menuApi.getUserMenus();
            if (res && Array.isArray(res.data)) {
              setMenu(res.data);
              setStorageItem(STORAGE_KEYS.MENU_DATA, res.data);
            } else {
              console.warn('⚠️ 菜单数据无效');
              setMenu([]);
            }
          } catch (error) {
            console.error('❌ 获取菜单失败:', error);
            setMenu([]);
          }
        }

        // 用户信息已在登录时获取并缓存，无需再次调用API
        devLog('✅ 使用缓存的用户数据');
      } finally {
        setLoading(false);
      }
    };

    initializeFromStorage();

    // 监听商城发来的 auth 同步消息
    const cleanup = listenForAuthMessages({
      onToken: async (token, refreshToken) => {
        // mall→admin SSO：商城回传 token，存储后重新初始化用户会话
        setToken(token);
        if (refreshToken) setRefreshToken(refreshToken);
        await initializeFromStorage();
      },
      onLogout: () => {
        // 商城发来登出通知 → 清理本地状态
        setUser(null);
        setMenu([]);
        clearUserData();
        window.location.href = '/login';
      },
      onTokenRefresh: (newToken, newRefreshToken) => {
        // 商城刷新了 token → 同步到本地
        setToken(newToken);
        if (newRefreshToken) setRefreshToken(newRefreshToken);
      },
    });

    // mall→admin SSO 反向：若管理后台由商城打开，向商城 opener 请求 token
    if (window.opener) {
      requestTokenFromOpener();
    }

    return cleanup;
  }, []);

  // 获取验证码
  const fetchCaptcha = async () => {
    return getCaptcha();
  };

  // 退出登录 - 调后端注销 + 通知商城 + 清前端缓存
  async function logout() {
    const token = getToken();

    // 1. 调后端登出接口（token 加入黑名单）
    if (token) {
      try {
        await unifiedApi.service('auth').post('/auth/logout', { token });
      } catch {
        console.warn('后端登出请求失败，继续前端清理');
      }
    }

    // 2. 通知商城窗口登出
    sendLogoutToMall();

    // 3. 清理前端状态和存储
    setUser(null);
    setMenu([]);
    clearUserData();
  }

  // 检查用户是否已认证（同时验证 token 有效性）
  const isAuthenticated = (): boolean => {
    // 先检查 token 是否存在且未过期
    const token = getToken();
    if (!token || isTokenExpired(token)) {
      // token 不存在或已过期，清理残留数据，返回未认证
      if (token) {
        devLog('🔒 Token 已过期，清理本地存储');
        clearUserData();
        setUser(null);
        setMenu([]);
      }
      return false;
    }

    if (user) {
      return true;
    }

    // 如果context中没有，则检查localStorage
    const userInfo = getStorageItem(STORAGE_KEYS.USER_INFO);
    return !!(userInfo && token);
  };

  // REQ-20260508-0001: 派生字段 — 从 user 提取门户路由信息
  const availablePortals: string[] =
    user && Array.isArray(user.available_portals) ? user.available_portals : [];
  const defaultPortal: string =
    user && typeof user.default_portal === 'string' ? user.default_portal : '';

  return (
    <AuthContext.Provider
      value={{
        login,
        selectTenant,
        savePortalPreference: handleSavePortalPreference,
        getCaptcha: fetchCaptcha,
        loading,
        menu,
        user,
        availablePortals,
        defaultPortal,
        logout,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export { AuthContext };
